import { useTheme } from 'canoe';
import { ENDPOINTS, useCreatePayload } from 'hammock';
import React, { useMemo } from 'react';
import { useQuery } from 'react-query';
import { Bar, BarChart, Legend, ResponsiveContainer, Tooltip, XAxis } from 'recharts';

import { MultiseriesApi } from '@candu-ai/analytics/content-events';
import { AnalyticQuery, QueryId, QueryParamUrlObject } from '@candu-ai/analytics/types';
import { getSeriesColor, GraphEmbed, HistogramTooltip } from '@candu-ai/analytics/ui';
import { createUseQuery } from '@candu-ai/analytics/utils';

interface Props {
  queryId: QueryId;
  queryParams: QueryParamUrlObject;
  multiseriesApi: MultiseriesApi;
  countLabel?: string;
}

export const HistogramGraph = ({
  queryId,
  queryParams,
  multiseriesApi,
  countLabel = 'Views',
}: Props) => {
  const { get } = useCreatePayload(ENDPOINTS.analyticQueries);
  const theme = useTheme();

  const namesPayload = get({
    id: queryId,
    query: queryParams,
  });
  const result = useQuery<AnalyticQuery<any>>(createUseQuery(namesPayload));
  const [xAxis, count, uniques] = result.data?.fields ?? [];
  const rows = result.data?.rows;

  const sortedRows = useMemo(() => {
    if (!rows) {
      return null;
    }
    const entries = multiseriesApi.getEntries();
    const rowsLookup = Object.fromEntries(rows.map((row: any) => [row[xAxis.name], row]));

    return entries.map((entry, index) => ({
      ...(rowsLookup[entry.value]
        ? rowsLookup[entry.value]
        : { [xAxis.name]: entry.value, [count.name]: 0, [uniques.name]: 0 }),
      index,
      label: entry.label,
      name: entry.label,
    }));
  }, [rows, multiseriesApi, xAxis, count, uniques]);

  if (!result.isLoading && (!rows || !sortedRows?.length)) {
    // failed to fetch query or data is faulty
    return null;
  }

  return (
    <div style={{ fontSize: '12px' }}>
      <GraphEmbed height={200} isLoading={result.isLoading}>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={sortedRows as any}>
            <Tooltip content={HistogramTooltip} />
            {/* show empty x-axis line */}
            <XAxis dataKey={xAxis?.name} tickFormatter={() => ''} />
            <Legend />
            <Bar name={countLabel} dataKey={count?.name} fill={theme.colors[getSeriesColor(0)]} />
            <Bar
              name={`Unique ${countLabel?.toLowerCase()}`}
              dataKey={uniques?.name}
              fill={theme.colors[getSeriesColor(1)]}
            />
          </BarChart>
        </ResponsiveContainer>
      </GraphEmbed>
    </div>
  );
};
